<template>
  <div class="column">
    <woot-modal-header :header-title="$t('AUTOMATION.EDIT.TITLE')" />
    <div class="row modal-content">
      <div class="medium-12 columns">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: $v.automation.name.$error }"
          :error="
            $v.automation.name.$error
              ? $t('AUTOMATION.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="$v.automation.name.$touch"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: $v.automation.description.$error }"
          :error="
            $v.automation.description.$error
              ? $t('AUTOMATION.ADD.FORM.DESC.ERROR')
              : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          @blur="$v.automation.description.$touch"
        />
        <div class="event_wrapper">
          <label :class="{ error: $v.automation.event_name.$error }">
            {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            <select v-model="automation.event_name" @change="onEventChange()">
              <option
                v-for="event in automationRuleEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.value }}
              </option>
            </select>
            <span v-if="$v.automation.event_name.$error" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </label>
        </div>
        <!-- // Conditions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
          </label>
          <div class="medium-12 columns filters-wrap">
            <filter-input-box
              v-for="(condition, i) in automation.conditions"
              :key="i"
              v-model="automation.conditions[i]"
              :grouped-filters="true"
              :filter-groups="
                formatAttributesToGroup(getAttributes(automation.event_name))
              "
              :filter-attributes="getAttributes(automation.event_name)"
              :input-type="getInputType(automation.conditions[i].attribute_key)"
              :operators="getOperators(automation.conditions[i].attribute_key)"
              :dropdown-values="
                getConditionDropdownValues(
                  automation.conditions[i].attribute_key
                )
              "
              :customDropDownValue="
                getCustomDropdownValue(automation.conditions[i])
              "
              :customEventDateDaysValue="
                getCustomEventDateDaysValue(automation.conditions[i])
              "
              :custom-attribute-type="
                getCustomAttributeType(automation.conditions[i].attribute_key)
              "
              :show-query-operator="i !== automation.conditions.length - 1"
              :show-user-input="
                showUserInput(automation.conditions[i].filter_operator)
              "
              :v="$v.automation.conditions.$each[i]"
              @resetFilter="resetFilter(i, automation.conditions[i])"
              @removeFilter="removeFilter(i)"
            />
            <div class="filter-actions">
              <woot-button
                icon="add"
                color-scheme="success"
                variant="smooth"
                size="small"
                @click="appendNewCondition"
              >
                {{ $t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL') }}
              </woot-button>
            </div>
          </div>
        </section>
        <!-- // Conditions End -->
        <!-- // Actions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </label>
          <div v-if="automation" class="medium-12 columns filters-wrap">
            <!-- :action-types="automationActionTypes" -->
            <automation-action-input
              v-for="(action, i) in automation.actions"
              :key="i"
              v-model="automation.actions[i]"
              :action-types="fetchActionTypes()"
              :custom-attributes="formatCustomAttributes(filterGroups)"
              :dropdown-values="
                getActionDropdownValues(automation.actions[i].action_name)
              "
              :show-action-input="showActionInput(action.action_name)"
              :v="$v.automation.actions.$each[i]"
              :initial-file-name="getFileName(action, automation.files)"
              @resetAction="resetAction(i)"
              @removeAction="removeAction(i)"
            />
            <div class="filter-actions">
              <woot-button
                icon="add"
                color-scheme="success"
                variant="smooth"
                size="small"
                @click="appendNewAction"
              >
                {{ $t('AUTOMATION.ADD.ACTION_BUTTON_LABEL') }}
              </woot-button>
            </div>
          </div>
        </section>
        <!-- // Actions End -->
        <div class="medium-12 columns">
          <div class="modal-footer justify-content-end w-full">
            <woot-button
              class="button"
              variant="clear"
              @click.prevent="onClose"
            >
              {{ $t('AUTOMATION.EDIT.CANCEL_BUTTON_TEXT') }}
            </woot-button>
            <woot-button @click="submitAutomation">
              {{ $t('AUTOMATION.EDIT.SUBMIT') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import automationMethodsMixin from 'dashboard/mixins/automations/methodsMixin';
import automationValidationsMixin from 'dashboard/mixins/automations/validationsMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import filterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import automationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
  CUSTOM_ATTRIBUTES_DATA,
} from './constants';
import conditionMixin from '../../../../../shared/mixins/conditionMixin';
import * as OPERATORS from '../../../../components/widgets/FilterInput/FilterOperatorTypes';

export default {
  components: {
    filterInputBox,
    automationActionInput,
  },
  mixins: [
    alertMixin,
    automationMethodsMixin,
    automationValidationsMixin,
    conditionMixin,
  ],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  validations: {
    automation: {
      name: {
        required,
      },
      description: {
        required,
      },
      event_name: {
        required,
      },
      conditions: {
        required,
        $each: {
          values: {
            required: requiredIf(prop => {
              return !(
                prop.filter_operator === 'is_present' ||
                prop.filter_operator === 'is_not_present'
              );
            }),
          },
          custom_event_name: {
            required: requiredIf(prop => {
              return !(prop.attribute_key !== 'custom_event');
            }),
          },
          days: {
            required: requiredIf(prop => {
              return !(prop.attribute_key !== 'custom_event');
            }),
          },
        },
      },
      actions: {
        required,
        $each: {
          action_params: {
            required,
          },
          notNeeded: {
            required: requiredIf(function(prop) {
              return this.fetchNameError(prop);
            }),
          },
        },
      },
    },
  },
  data() {
    return {
      automationTypes: JSON.parse(JSON.stringify(AUTOMATIONS)),
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationRuleEvents: AUTOMATION_RULE_EVENTS,
      automationActionTypes: AUTOMATION_ACTION_TYPES,
      automationMutated: false,
      show: true,
      attributeModel: 'contact_attribute',
      filtersFori18n: 'CONTACTS_FILTER',
      filterGroups: [],
      filterTypes: [],
      // automation: null,

      automation: {
        name: null,
        /*
        description: null,
        event_name: 'conversation_created',
        conditions: [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ],
        actions: [
          {
            action_name: 'assign_team',
            action_params: [],
          },
        ],
        */
      },

      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      mode: 'edit',
    };
  },
  computed: {
    /*
    conditions() {
      return this.automationTypes[this.automation.event_name].conditions;
    },
    actions() {
      return this.automationTypes[this.automation.event_name].actions;
    },
    filterAttributes() {
      return this.filterTypes.map(type => {
        return {
          key: type.attributeKey,
          name: type.attributeName,
          attributeI18nKey: type.attributeI18nKey,
        };
      });
    },
    */
  },
  mounted() {
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];
    this.manifestCustomAttributes();
    // this.formatConditions(this.selectedResponse);
    this.setConditionAttributes(
      this.automationTypes[this.selectedResponse.event_name].conditions
    );
    this.formatAutomation(this.selectedResponse);
  },
  methods: {
    /*
    formatAttributesToGroup(attributeList) {
      const mainAttributeList = [
        {
          key: "standard_attribute",
          name: "Standard Attributes",
          disabled: true
        },
        ...attributeList,
      ];
      const groupedList = [
        {
          key: "standard_attribute",
          name: "Standard Attributes",
          attributes: [],
        },
        {
          key: "conversation_custom_attribute",
          name: "Conversation Custom Attributes",
          attributes: [],
        },
        {
          key: "contact_custom_attribute",
          name: "Contact Custom Attributes",
          attributes: [],
        },
      ];

      console.log({attributeList,mainAttributeList});

      mainAttributeList.forEach((attribute) => {
        if (attribute.disabled) {
          // Skip elements with disabled: true
        } else if (attribute.customAttributeType === "conversation_attribute") {
          groupedList[1].attributes.push(attribute);
        } else if (attribute.customAttributeType === "contact_attribute") {
          groupedList[2].attributes.push(attribute);
        } else {
          groupedList[0].attributes.push(attribute);
        }
      });

      return groupedList;
    },
    */

    onEventChange() {
      if (
        this.automation.event_name === 'message_created' ||
        this.automation.event_name === 'message_updated'
      ) {
        this.automation.conditions = [
          {
            attribute_key: 'message_type',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'contact_created') {
        this.automation.conditions = [
          {
            attribute_key: 'email',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'contact_updated') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'contact_attribute_changed') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'form_responder_completed') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'event_created') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else {
        this.automation.conditions = [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }

      // initial action setup
      if (this.automation.event_name === 'conversation_updated') {
        this.automation.actions = [
          {
            action_name: 'add_label',
            action_params: [],
          },
        ];
      } else if (this.automation.event_name === 'event_created') {
        this.automation.actions = [
          {
            action_name: 'assign_bot',
            action_params: [],
          },
        ];
      } else {
        this.automation.actions = [
          {
            action_name: 'assign_team',
            action_params: [],
          },
        ];
      }
      // set condition attributes
      this.setConditionAttributes(
        this.automationTypes[this.automation.event_name].conditions
      );
    },
    fetchNameError(props) {
      const action_name =
        props !== undefined
          ? props.action_params
          : this.automation.actions[0].action_params;
      const data = this.automation.conditions.filter(item => {
        if (
          item.custom_event_name === '' ||
          item.custom_event_name === undefined
        ) {
          return false;
        }
        return item.custom_event_name === action_name;
      });
      return data.length > 0;
    },
    getCustomDropdownValue(condition) {
      if (condition.attribute_key !== 'custom_event') {
        return {};
      }

      const dropdownListDateRange = [
        {
          id: 'in_the_past',
          name: 'In the past',
        },
        {
          id: 'in_between',
          name: 'In between',
        },
      ];

      if (condition.days?.length === 1) {
        return dropdownListDateRange[0];
      }

      if (condition.days?.length === 2) {
        return dropdownListDateRange[1];
      }

      return {};
    },
    getCustomEventDateDaysValue(condition) {
      if (condition.attribute_key !== 'custom_event') {
        return [];
      }

      if (condition.days?.length === 1) {
        return condition.days[0];
      }

      if (condition.days?.length === 2) {
        return [...condition.days];
      }

      return [];
    },
    resetFilter(index, currentCondition) {
      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;
      this.automation.conditions[index].values = '';
    },
    appendNewCondition() {
      if (
        !this.automation.conditions[this.automation.conditions.length - 1]
          .query_operator
      ) {
        this.automation.conditions[
          this.automation.conditions.length - 1
        ].query_operator = 'and';
      }
      switch (this.automation.event_name) {
        case 'message_created':
        case 'message_updated':
          this.automation.conditions.push({
            attribute_key: 'message_type',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        case 'contact_created':
        case 'contact_updated':
        case 'contact_attribute_changed':
        case 'form_responder_completed':
          this.automation.conditions.push({
            attribute_key: 'email',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        case 'event_created':
          this.automation.conditions.push({
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        default:
          this.automation.conditions.push({
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
      }
    },
    fetchActionTypes() {
      let query_string = '';
      query_string += this.automation.conditions.map(value => {
        return (
          ' ' +
          value.attribute_key +
          ' ' +
          value.filter_operator +
          ' ' +
          value.values?.id
        );
      });
      if (query_string.includes('message_type equal_to outgoing')) {
        return this.automationTypes.message_updated.actions;
      }
      if (
        this.automation.event_name === 'message_created' &&
        this.$store.getters['inboxes/checkWebInbox'].length === 0
      ) {
        this.automationTypes[this.automation.event_name].actions.map(
          // eslint-disable-next-line array-callback-return
          (value, index) => {
            if (value.key === 'trigger_event') {
              this.automationTypes[this.automation.event_name].actions.splice(
                index,
                1
              );
            }
          }
        );
      }
      return this.automationTypes[this.automation.event_name].actions;
    },

    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        default:
          return 'plain_text';
      }
    },
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_5;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    formatCustomAttributes(customAttribute) {
      let attributes = CUSTOM_ATTRIBUTES_DATA;
      customAttribute.map(value => {
        if (value.name === 'Custom Attributes') {
          attributes = [...attributes, ...value.attributes];
        }
        return attributes;
      });
      attributes = attributes.map(el => {
        if (!Object.prototype.hasOwnProperty.call(el, 'id')) {
          el.id = el.key;
        }
        return el;
      });
      return attributes;
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    /*
    getAttributes(key) {
      return this.automationTypes[key].conditions;
    },
    getInputType(key) {
      const type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(condition => condition.key === key);
      return type.inputType;
    },
    getOperators(key) {
      const type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(condition => condition.key === key);
      return type.filterOperators;
    },
    getConditionDropdownValues(type) {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      switch (type) {
        case 'status':
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
            {
              id: 'all',
              name: this.$t('CHAT_LIST.FILTER_ALL'),
            },
          ];
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'contact':
          return this.$store.getters['contacts/getContacts'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'campaign_id':
          return this.$store.getters['campaigns/getAllCampaigns'].map(i => {
            return {
              id: i.id,
              name: i.title,
            };
          });
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.id,
              name: i.title,
            };
          });
        case 'browser_language':
          return languages;
        case 'country_code':
          return countries;
        case 'message_type':
          return [
            {
              id: 'incoming',
              name: 'Incoming Message',
            },
            {
              id: 'outgoing',
              name: 'Outgoing Message',
            },
          ];
        default:
          return undefined;
      }
    },
    getActionDropdownValues(type) {
      switch (type) {
        case 'assign_team':
        case 'send_email_to_team':
          return this.$store.getters['teams/getTeams'];
        case 'add_label':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        default:
          return undefined;
      }
    },
    appendNewCondition() {
      if (
        !this.automation.conditions[this.automation.conditions.length - 1]
          .query_operator
      ) {
        this.automation.conditions[
          this.automation.conditions.length - 1
        ].query_operator = 'and';
      }
      switch (this.automation.event_name) {
        case 'message_created':
          this.automation.conditions.push({
            attribute_key: 'message_type',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        case 'contact_created':
        case 'contact_updated':
          this.automation.conditions.push({
            attribute_key: 'email',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        default:
          this.automation.conditions.push({
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
      }
    },
    appendNewAction() {
      this.automation.actions.push({
        action_name: 'assign_team',
        action_params: [],
      });
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.ACTION.DELETE_MESSAGE'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.automation.conditions[
        this.automation.conditions.length - 1
      ].query_operator = null;
      this.automation.conditions = filterQueryGenerator(
        this.automation.conditions
      ).payload;
      this.automation.actions = actionQueryGenerator(this.automation.actions);
      this.$emit('saveAutomation', this.automation, 'EDIT');
    },
    resetFilter(index, currentCondition) {
      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;
      this.automation.conditions[index].values = '';
    },
    formatConditions(automation) {
      console.log({automation});
      const formattedConditions = automation.conditions.map(condition => {
        const inputType = this.automationTypes[
          automation.event_name
        ].conditions.find(item => item.key === condition.attribute_key)
          .inputType;
        if (inputType === 'plain_text') {
          return {
            ...condition,
            values: condition.values[0],
          };
        }
        return {
          ...condition,
          values: [
            ...this.getConditionDropdownValues(condition.attribute_key),
          ].filter(item => [...condition.values].includes(item.id)),
        };
      });
      const formattedActions = automation.actions.map(action => {
        return {
          ...action,
          action_params: [
            ...this.getActionDropdownValues(action.action_name),
          ].filter(item => [...action.action_params].includes(item.id)),
        };
      });
      this.automation = {
        ...automation,
        conditions: formattedConditions,
        actions: formattedActions,
      };
    },
    */
  },
};
</script>
<style lang="scss" scoped>
.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-border);
  background: var(--color-background-light);
  margin-bottom: var(--space-normal);
}

.filter-actions {
  margin-top: var(--space-normal);
}
.event_wrapper {
  select {
    margin: var(--space-zero);
  }
  .info-message {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    text-align: right;
  }
  margin-bottom: var(--space-medium);
}
</style>
